import { useCallback, useEffect, useState, Fragment } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Download, RefreshCw, Send, X } from "lucide-react";

function fmtDate(iso) {
    try { return iso ? new Date(iso).toLocaleString() : "—"; } catch { return iso; }
}

const FILTERS = [
    { key: "all", label: "All Self-Funded" },
    { key: "pending", label: "Pending Payments" },
    { key: "confirmed", label: "Confirmed Delegates" },
    { key: "failed", label: "Failed" },
];

const STATUS_STYLE = {
    confirmed: "text-[var(--emerald)]",
    payment_pending: "text-[var(--gold)]",
    payment_failed: "text-red-400",
    paid_needs_review: "text-red-300",
};

const STATUS_LABEL = {
    confirmed: "Confirmed",
    payment_pending: "Payment pending",
    payment_failed: "Payment failed",
    paid_needs_review: "Paid · needs review",
};

export default function SelfFundedPanel() {
    const [overview, setOverview] = useState(null);
    const [settings, setSettings] = useState(null);
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [q, setQ] = useState("");
    const [seatInput, setSeatInput] = useState("");
    const [journey, setJourney] = useState(null); // { email, events } | null
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [ov, st, rg] = await Promise.all([
                api.get("/admin/cps/overview"),
                api.get("/admin/cps/self-funded/settings"),
                api.get("/admin/registrations", { params: { status: filter === "all" ? undefined : filter, q: q || undefined } }),
            ]);
            setOverview(ov.data);
            setSettings(st.data);
            setSeatInput(st.data.max_seats ?? "");
            setItems(rg.data.items);
        } catch (e) {
            toast.error("Could not load self-funded data");
        } finally {
            setLoading(false);
        }
    }, [filter, q]);

    useEffect(() => { load(); }, [load]);

    const saveSettings = async (open, maxSeats) => {
        try {
            await api.put("/admin/cps/self-funded/settings", {
                open,
                max_seats: maxSeats === "" || maxSeats === null ? null : Number(maxSeats),
            });
            toast.success("Settings saved");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save settings");
        }
    };

    const resend = async (reg) => {
        try {
            await api.post(`/admin/registrations/${reg.id}/resend-confirmation`);
            toast.success(`Confirmation resent to ${reg.email}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Resend failed");
        }
    };

    const openJourney = async (email) => {
        try {
            const { data } = await api.get(`/admin/contacts/${encodeURIComponent(email)}/journey`);
            setJourney(data);
        } catch {
            toast.error("Could not load journey");
        }
    };

    const exportCsv = async () => {
        try {
            const res = await api.get("/admin/export/registrations.csv", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "zsgi-registrations.csv";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Registrations exported");
        } catch {
            toast.error("Export failed");
        }
    };

    const cards = overview ? [
        { label: "Brochure Downloads", value: overview.brochure_downloads, testid: "sf-stat-brochures" },
        { label: "Scholarship Applications", value: overview.scholarship_applications, testid: "sf-stat-scholarship" },
        { label: "Self-Funded Registrations", value: overview.self_funded_total, testid: "sf-stat-total" },
        { label: "Confirmed Delegates", value: overview.self_funded_confirmed, testid: "sf-stat-confirmed" },
        { label: "Pending Payments", value: overview.self_funded_pending, testid: "sf-stat-pending" },
        { label: "Self-Funded Revenue (verified)", value: `₹${overview.self_funded_revenue_inr.toLocaleString("en-IN")}`, testid: "sf-stat-revenue" },
        { label: "Total CPS Collections", value: `₹${overview.cps_collections.INR.toLocaleString("en-IN")}${overview.cps_collections.USD ? ` + $${overview.cps_collections.USD}` : ""}`, testid: "sf-stat-collections" },
    ] : [];

    return (
        <div data-testid="self-funded-panel">
            <div className="flex items-center justify-between mb-6">
                <h1 className="serif-display text-3xl md:text-4xl">CPS 2026 · Self-Funded</h1>
                <div className="flex gap-3">
                    <button onClick={load} className="btn-ghost" data-testid="sf-reload" disabled={loading}>
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </button>
                    <button onClick={exportCsv} className="btn-ghost" data-testid="export-registrations">
                        <Download className="w-4 h-4" /> Export CSV
                    </button>
                </div>
            </div>

            {/* Revenue & registration overview */}
            <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-[rgba(255,255,240,0.12)] mb-10" data-testid="sf-overview-cards">
                {cards.map((c) => (
                    <div key={c.label} className="p-5 border-r border-b border-[rgba(255,255,240,0.12)]" data-testid={c.testid}>
                        <div className="serif-display text-3xl text-[var(--gold)]">{c.value}</div>
                        <div className="label-mono mt-2 text-[var(--ivory)]/60 text-[10px]">{c.label}</div>
                    </div>
                ))}
                {/* Slot management lives in the same grid */}
                <div className="p-5 border-r border-b border-[rgba(255,255,240,0.12)]" data-testid="sf-slot-card">
                    <div className="label-mono text-[var(--ivory)]/60 text-[10px] mb-3">REGISTRATION SLOTS</div>
                    {settings && (
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => saveSettings(!settings.open, settings.max_seats)}
                                    className={`label-mono text-[10px] px-4 py-2 border transition-colors ${settings.open ? "border-[var(--emerald)] text-[var(--emerald)]" : "border-red-400 text-red-400"}`}
                                    data-testid="sf-toggle-open"
                                >
                                    {settings.open ? "OPEN" : "CLOSED"}
                                </button>
                                <span className="label-mono text-[10px] text-[var(--ivory)]/60" data-testid="sf-seat-counts">
                                    {settings.confirmed} confirmed{settings.seats_left != null ? ` · ${settings.seats_left} left` : " · uncapped"}
                                </span>
                            </div>
                            <div className="flex gap-2">
                                <input
                                    type="number"
                                    min="0"
                                    placeholder="Max seats (blank = ∞)"
                                    title="Leave blank for unlimited seats"
                                    className="input-editorial !py-2 text-sm flex-1"
                                    value={seatInput}
                                    onChange={(e) => setSeatInput(e.target.value)}
                                    data-testid="sf-max-seats-input"
                                />
                                <button
                                    onClick={() => saveSettings(settings.open, seatInput)}
                                    className="label-mono text-[10px] px-4 py-2 border border-[var(--gold)] text-[var(--gold)]"
                                    data-testid="sf-save-seats"
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="sf-filters">
                {FILTERS.map((f) => (
                    <button
                        key={f.key}
                        onClick={() => setFilter(f.key)}
                        className={`label-mono text-[10px] px-4 py-2 border transition-colors ${filter === f.key ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(201,162,75,0.08)]" : "border-[rgba(255,255,240,0.15)] text-[var(--ivory)]/60 hover:text-[var(--ivory)]"}`}
                        data-testid={`sf-filter-${f.key}`}
                    >
                        {f.label}
                    </button>
                ))}
                <input
                    className="input-editorial !py-2 text-sm ml-auto w-full sm:w-64"
                    placeholder="Search name, email or registration ID…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    data-testid="sf-search"
                />
            </div>

            {/* Registrations table */}
            {!items.length ? (
                <div className="label-mono text-[var(--ivory)]/40 py-16 text-center" data-testid="sf-table-empty">NO REGISTRATIONS YET</div>
            ) : (
                <div className="overflow-x-auto border-t border-l border-[rgba(255,255,240,0.12)]" data-testid="sf-table">
                    <div className="min-w-[1100px]">
                        <div className="grid text-left" style={{ gridTemplateColumns: "1.2fr 1.4fr 1.6fr 1.3fr 1fr 1.2fr 0.9fr 1fr 1.2fr 1.4fr" }}>
                            {["Reg ID", "Name", "Email", "WhatsApp", "Country", "Institution", "Amount", "Status", "Registered", "Actions"].map((h) => (
                                <div key={h} className="label-mono text-[var(--gold)] p-3 border-r border-b border-[rgba(255,255,240,0.12)] bg-[var(--navy-deep)]">{h}</div>
                            ))}
                            {items.map((r) => (
                                <Fragment key={r.id}>
                                    <div key={`ref-${r.id}`} className="p-3 border-r border-b border-[rgba(255,255,240,0.12)] text-sm text-[var(--gold)] break-words" data-testid={`sf-row-ref-${r.ref}`}>{r.ref}</div>
                                    <div key={`name-${r.id}`} className="p-3 border-r border-b border-[rgba(255,255,240,0.12)] text-sm text-[var(--ivory)]/80 break-words">
                                        {r.full_name}
                                        {(r.dietary && r.dietary !== "No preference") || r.accommodation ? (
                                            <div className="text-[11px] text-[var(--ivory)]/50 mt-1">
                                                {[r.dietary !== "No preference" ? r.dietary : null, r.accommodation].filter(Boolean).join(" · ")}
                                            </div>
                                        ) : null}
                                    </div>
                                    <div key={`email-${r.id}`} className="p-3 border-r border-b border-[rgba(255,255,240,0.12)] text-sm text-[var(--ivory)]/80 break-words">{r.email}</div>
                                    <div key={`wa-${r.id}`} className="p-3 border-r border-b border-[rgba(255,255,240,0.12)] text-sm text-[var(--ivory)]/80 break-words">{r.whatsapp}</div>
                                    <div key={`cty-${r.id}`} className="p-3 border-r border-b border-[rgba(255,255,240,0.12)] text-sm text-[var(--ivory)]/80 break-words">{r.residence_country}</div>
                                    <div key={`inst-${r.id}`} className="p-3 border-r border-b border-[rgba(255,255,240,0.12)] text-sm text-[var(--ivory)]/80 break-words">{r.institution || "—"}</div>
                                    <div key={`amt-${r.id}`} className="p-3 border-r border-b border-[rgba(255,255,240,0.12)] text-sm text-[var(--ivory)]/80">₹{(r.amount || 0).toLocaleString("en-IN")}</div>
                                    <div key={`st-${r.id}`} className={`p-3 border-r border-b border-[rgba(255,255,240,0.12)] text-sm ${STATUS_STYLE[r.status] || "text-[var(--ivory)]/60"}`} data-testid={`sf-row-status-${r.ref}`}>
                                        {STATUS_LABEL[r.status] || r.status}
                                        {r.payment_id && <div className="text-[11px] text-[var(--ivory)]/50 mt-1 break-all">{r.payment_id}</div>}
                                        {r.status === "confirmed" && (
                                            <div className="text-[11px] mt-1" style={{ color: r.confirmation_email_sent ? "var(--emerald)" : "var(--gold)" }} data-testid={`sf-email-status-${r.ref}`}>
                                                {r.confirmation_email_sent ? "✓ confirmation email sent" : "confirmation email pending"}
                                            </div>
                                        )}
                                    </div>
                                    <div key={`at-${r.id}`} className="p-3 border-r border-b border-[rgba(255,255,240,0.12)] text-sm text-[var(--ivory)]/80">{fmtDate(r.created_at)}</div>
                                    <div key={`act-${r.id}`} className="p-3 border-r border-b border-[rgba(255,255,240,0.12)] text-sm">
                                        <div className="flex flex-col gap-2">
                                            <button onClick={() => openJourney(r.email)} className="label-mono text-[10px] text-[var(--gold)] text-left hover:underline" data-testid={`sf-journey-${r.ref}`}>
                                                View journey
                                            </button>
                                            {r.status === "confirmed" && (
                                                <button onClick={() => resend(r)} className="label-mono text-[10px] text-[var(--ivory)]/70 text-left hover:underline inline-flex items-center gap-1" data-testid={`sf-resend-${r.ref}`}>
                                                    <Send className="w-3 h-3" /> Resend confirmation
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Journey modal */}
            {journey && (
                <div className="fixed inset-0 z-[80] bg-[rgba(6,17,36,0.8)] backdrop-blur-md flex items-center justify-center p-4" onClick={() => setJourney(null)} data-testid="sf-journey-overlay">
                    <div className="w-full max-w-lg bg-[var(--navy)] border border-[var(--gold)] max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sf-journey-modal">
                        <div className="p-6 border-b border-[rgba(255,255,240,0.12)] flex items-start justify-between">
                            <div>
                                <div className="label-mono text-[var(--gold)]">— CONTACT JOURNEY</div>
                                <div className="serif-display text-xl mt-2 break-all">{journey.email}</div>
                            </div>
                            <button onClick={() => setJourney(null)} className="text-[var(--ivory)]/60 hover:text-[var(--ivory)]" data-testid="sf-journey-close"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-6">
                            {!journey.events.length ? (
                                <div className="label-mono text-[var(--ivory)]/40 text-center py-8">NO ACTIVITY RECORDED</div>
                            ) : (
                                <div className="space-y-0 border-l border-[rgba(201,162,75,0.35)]">
                                    {journey.events.map((ev, i) => (
                                        <div key={i} className="pl-5 pb-5 relative">
                                            <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 bg-[var(--gold)]" />
                                            <div className="label-mono text-[10px] text-[var(--gold)]">{ev.type}</div>
                                            <div className="text-sm text-[var(--ivory)]/80 mt-1">{ev.detail}</div>
                                            <div className="label-mono text-[10px] text-[var(--ivory)]/40 mt-1">{fmtDate(ev.at)}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
